import { Injectable } from '@angular/core';
import {
    FlyFreelyError,
    FlyFreelyLoggingService,
    LocationFeatureDto
} from '@flyfreely-portal-ui/flyfreely';
import { toPosition } from 'libs/map/src/lib/utils';
import { z } from 'zod';
import { UGCS_MISSION_SCHEMA } from './schemas';

export type UgCSMissionData = z.infer<typeof UGCS_MISSION_SCHEMA>;

export interface UgCSValidationResult {
    isValid: boolean;
    errors?: string[];
    data?: UgCSMissionData;
}

@Injectable()
export class UgcsUploadService {
    constructor(private logging: FlyFreelyLoggingService) {}

    getMissionName(ugcsData: UgCSMissionData): string | null {
        return ugcsData.mission?.name || null;
    }

    /**
     * Validates and parses UgCS JSON data
     * @param jsonString Raw JSON string from uploaded file
     * @returns Validation result with parsed data
     * @throws UgCSValidationError if validation fails
     * @throws UgCSParseError if JSON parsing fails
     */
    validateAndParseJSON(jsonString: string): UgCSValidationResult {
        let parsedData: unknown;

        try {
            parsedData = JSON.parse(jsonString);
        } catch (error) {
            throw new FlyFreelyError('Invalid file format');
        }

        const result = UGCS_MISSION_SCHEMA.safeParse(parsedData);

        if (!result.success) {
            throw new FlyFreelyError(
                'Uploaded file does not match known type'
            );
        }

        return {
            isValid: true,
            data: result.data
        };
    }

    convertToLocationFeatures(ugcsData: UgCSMissionData): LocationFeatureDto[] {
        const features: LocationFeatureDto[] = [];

        ugcsData.mission.routes.forEach(route => {
            route.segments.forEach(segment => {
                const feature = this.createLocationFeature(segment, route.name);
                if (feature != null) {
                    features.push(feature);
                }
            });
        });

        if (features.length === 0) {
            throw new FlyFreelyError(
                'No valid features found in UgCS mission plan'
            );
        }

        return features;
    }

    private createLocationFeature(
        segment: UgCSMissionData['mission']['routes'][0]['segments'][0],
        routeName: string
    ): LocationFeatureDto | null {
        const featureName = `${routeName}-${segment.type}`;

        switch (segment.type) {
            case 'TakeOff':
                if (segment.point) {
                    return {
                        name: featureName,
                        type: LocationFeatureDto.Type.TAKEOFF_LANDING,
                        geometry: {
                            type: 'Point',
                            coordinates: toPosition({
                                latitude: segment.point.latitude,
                                longitude: segment.point.longitude
                            })
                        }
                    };
                }
                break;
            case 'Landing':
                if (segment.point) {
                    return {
                        name: featureName,
                        type: LocationFeatureDto.Type.LANDING,
                        geometry: {
                            type: 'Point',
                            coordinates: toPosition({
                                latitude: segment.point.latitude,
                                longitude: segment.point.longitude
                            })
                        }
                    };
                }
                break;
            case 'AreaScan':
                if (segment.polygon && segment.polygon.points?.length >= 3) {
                    const coordinates = segment.polygon.points.map(point =>
                        toPosition({
                            latitude: point.latitude,
                            longitude: point.longitude
                        })
                    );
                    return {
                        name: featureName,
                        type: LocationFeatureDto.Type.FLIGHT_AREA,
                        geometry: {
                            type: 'Polygon',
                            coordinates: [coordinates]
                        }
                    };
                }
                break;

            default:
                this.logging.warn(`Unknown UgCS segment type: ${segment.type}`);
                return null;
        }

        this.logging.warn(
            `Invalid segment data for type ${segment.type}: missing required geometry`
        );
        return null;
    }
}
